/**
 * Voice Controls Component
 *
 * Control buttons for voice calls:
 * - Mute/Unmute microphone
 * - Deafen/Undeafen (disable audio output)
 * - Screen share toggle
 * - Disconnect from call
 */

import React from 'react';
import { useVoice } from '@/contexts/VoiceContext';
import { Mic, MicOff, Volume2, VolumeX, Monitor, PhoneOff, Circle } from 'lucide-react';

export const VoiceControls: React.FC = () => {
  const {
    voiceState,
    toggleMute,
    toggleDeafen,
    toggleScreenShare,
    leaveSession,
    startRecording,
    stopRecording,
    formatRecordingDuration
  } = useVoice();

  const handleRecordingToggle = async () => {
    if (voiceState.isRecording) {
      await stopRecording();
    } else {
      await startRecording({ includeVideo: false }); // Audio-only by default
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Recording status indicator */}
      {voiceState.isRecording && (
        <div className="flex items-center space-x-2 px-4 py-2 bg-red-900/30 rounded-full border border-red-500/50">
          <Circle
            className="w-3 h-3 text-red-500 fill-red-500 animate-pulse"
          />
          <span className="text-sm font-medium text-red-200">
            Запис: {formatRecordingDuration(voiceState.recordingDuration)}
          </span>
        </div>
      )}

      {/* Control buttons */}
      <div className="flex items-center justify-center space-x-4">
        {/* Recording button */}
        <button
          onClick={handleRecordingToggle}
          className={`p-3 rounded-full transition-all ${
            voiceState.isRecording
              ? 'bg-red-600 hover:bg-red-700 animate-pulse'
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
          title={voiceState.isRecording ? 'Спри запис' : 'Започни запис'}
          aria-label={voiceState.isRecording ? 'Спри запис' : 'Започни запис'}
        >
          <Circle
            className={`w-5 h-5 ${
              voiceState.isRecording
                ? 'text-white fill-white'
                : 'text-white'
            }`}
          />
        </button>

        {/* Mute button */}
        <button
          onClick={toggleMute}
          className={`p-3 rounded-full transition-colors ${
            voiceState.isMuted
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
          title={voiceState.isMuted ? 'Включи микрофона' : 'Изключи микрофона'}
          aria-label={voiceState.isMuted ? 'Включи микрофона' : 'Изключи микрофона'}
        >
          {voiceState.isMuted ? (
            <MicOff className="w-5 h-5 text-white" />
          ) : (
            <Mic className="w-5 h-5 text-white" />
          )}
        </button>

      {/* Deafen button */}
      <button
        onClick={toggleDeafen}
        className={`p-3 rounded-full transition-colors ${
          voiceState.isDeafened
            ? 'bg-red-600 hover:bg-red-700'
            : 'bg-gray-600 hover:bg-gray-700'
        }`}
        title={voiceState.isDeafened ? 'Включи звука' : 'Изключи звука'}
        aria-label={voiceState.isDeafened ? 'Включи звука' : 'Изключи звука'}
      >
        {voiceState.isDeafened ? (
          <VolumeX className="w-5 h-5 text-white" />
        ) : (
          <Volume2 className="w-5 h-5 text-white" />
        )}
      </button>

      {/* Screen share button */}
      <button
        onClick={toggleScreenShare}
        className={`p-3 rounded-full transition-colors ${
          voiceState.isScreenSharing
            ? 'bg-blue-600 hover:bg-blue-700'
            : 'bg-gray-600 hover:bg-gray-700'
        }`}
        title={voiceState.isScreenSharing ? 'Спри споделянето' : 'Сподели екрана'}
        aria-label={voiceState.isScreenSharing ? 'Спри споделянето на екрана' : 'Сподели екрана'}
      >
        <Monitor className="w-5 h-5 text-white" />
      </button>

        {/* Disconnect button */}
        <button
          onClick={leaveSession}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
          title="Прекрати връзката"
          aria-label="Прекрати връзката с обаждането"
        >
          <PhoneOff className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  );
};
